import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PersonOutline from '@mui/icons-material/PersonOutline';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { AuthContext } from '../../contexts/AuthContext';
import { ConfirmDialog } from '../ui';

/**
 * Top bar. Navy, full-bleed, fixed.
 *
 * It carries identity only — logo, clock, account menu. Module links are not
 * duplicated here: screens are reached from the Home grid on desktop and the
 * bottom tab bar on mobile.
 */
function Navbar() {
  const { isLoggedIn, logOut, username, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [now, setNow] = useState(new Date());
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Close the account menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogOut = async () => {
    setConfirmLogout(false);
    await logOut();
    navigate('/login');
  };

  const time = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const date = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(now);

  return (
    <>
      <header className="z-40 h-nav border-b border-white/10 bg-primary">
        <div className="mx-auto flex h-full max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link
            to="/"
            className="flex h-11 shrink-0 items-center gap-2.5 rounded-md pr-2 transition-opacity duration-fast hover:opacity-80"
          >
            <img src="/images/rgalogo.png" className="h-7 w-7 object-contain" alt="" />
            <span className="text-base font-semibold tracking-tight text-white">RGA Reports</span>
          </Link>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden text-right leading-tight sm:block">
              <div className="tabular text-sm font-medium text-white">{time}</div>
              <div className="text-xs text-white/60">{date}</div>
            </div>

            {!isLoggedIn ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="flex min-h-11 items-center rounded-md px-3 text-sm font-medium text-white/80 transition-colors duration-fast hover:bg-white/10 hover:text-white"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="flex min-h-11 items-center rounded-md bg-accent px-4 text-sm font-medium text-accent-fg transition-colors duration-fast hover:bg-accent-hover"
                >
                  Request access
                </Link>
              </div>
            ) : (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-expanded={menuOpen}
                  aria-haspopup="menu"
                  aria-label="Account menu"
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-2 text-sm font-medium text-white transition-colors duration-fast hover:bg-white/10"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-fg">
                    <PersonOutline style={{ fontSize: 18 }} />
                  </span>
                  <span className="hidden max-w-[10rem] truncate sm:inline">{username || 'Account'}</span>
                </button>

                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-56 origin-top-right animate-fade-in overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
                  >
                    <div className="border-b border-border px-4 py-3">
                      <p className="truncate text-sm font-semibold text-foreground">{username}</p>
                      <p className="mt-0.5 text-xs capitalize text-muted-fg">{role || 'user'}</p>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => navigate('/settings')}
                      className="flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 text-sm text-foreground transition-colors duration-fast hover:bg-muted"
                    >
                      <SettingsOutlined style={{ fontSize: 18 }} className="text-muted-fg" />
                      Settings
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmLogout(true);
                      }}
                      className="flex min-h-11 w-full cursor-pointer items-center gap-3 border-t border-border px-4 text-sm text-danger transition-colors duration-fast hover:bg-danger-soft"
                    >
                      <LogoutOutlined style={{ fontSize: 18 }} />
                      Log out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <ConfirmDialog
        isOpen={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={handleLogOut}
        title="Log out"
        message="You will need to sign in again to access your reports."
        confirmLabel="Log out"
      />
    </>
  );
}

export default Navbar;
